import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBook,
  faChartLine,
  faKey,
  faLayerGroup,
  faPlus,
  faUsers,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import {
  getLibraryLessons,
  getTeacherBillingSubscription,
  getTeacherCohortAssignmentProgress,
  getTeacherCohortAssignments,
  getTeacherCohorts,
  getTeacherCourses,
  type TeacherBillingSubscriptionResponse,
  type TeacherCohortAssignmentRecord,
  type TeacherCohortRecord,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { parseApiInstant } from "../datetime/parseApiInstant";
import {
  aggregateProgress,
  lessonRowsFromBundles,
  teacherAssignmentReviewUrl,
  type CohortProgressBundle,
} from "./submissions/submissionsAggregate";
import { countPlanItems, formatPlanItemCounts, lessonAndQuizItems } from "./submissions/cohortProgressCounts";
import ProgressDonut from "./charts/ProgressDonut";
import { StackedProgressTrack } from "./charts/StackedProgressBar";
import { ASSIGNMENT_PROGRESS_EMOJI } from "../student/assignmentProgressStatus";
import { TeacherOverviewSkeleton } from "./TeacherOverviewSkeleton";

const DAY_MS = 24 * 60 * 60 * 1000;

type DeadlineRow = {
  cohortId: string;
  cohortLabel: string;
  assignment: TeacherCohortAssignmentRecord;
  dueAt: Date;
  daysFromNow: number;
};

function greetingForHour(hour: number): string {
  if (hour < 5) return "Burning the midnight oil";
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function firstNameFrom(name: string | undefined, email: string | undefined): string | null {
  const fromName = name?.trim().split(/\s+/)[0];
  if (fromName) return fromName;
  const fromEmail = email?.split("@")[0];
  return fromEmail ? fromEmail : null;
}

function pct(n: number | null): string {
  return n === null ? "-" : `${n}%`;
}

function dueLabel(daysFromNow: number): { text: string; tone: "overdue" | "soon" | "later" } {
  if (daysFromNow < 0) {
    const d = Math.abs(daysFromNow);
    return { text: d === 1 ? "1 day overdue" : `${d} days overdue`, tone: "overdue" };
  }
  if (daysFromNow === 0) return { text: "Due today", tone: "soon" };
  if (daysFromNow === 1) return { text: "Due tomorrow", tone: "soon" };
  if (daysFromNow <= 7) return { text: `Due in ${daysFromNow} days`, tone: "soon" };
  return { text: `Due in ${daysFromNow} days`, tone: "later" };
}

function CohortPulseRow({ bundle }: { bundle: CohortProgressBundle }) {
  const tracked = lessonAndQuizItems(bundle.assignments);
  let complete = 0;
  let inProgress = 0;
  let needsHelp = 0;
  let notStarted = 0;
  for (const a of tracked) {
    complete += a.complete;
    inProgress += a.inProgress;
    needsHelp += a.needsHelp;
    notStarted += a.notStarted;
  }
  const total = complete + inProgress + needsHelp + notStarted;
  const passRate = total > 0 ? Math.round((complete / total) * 100) : null;

  return (
    <Link to={`/teacher/cohorts/${encodeURIComponent(bundle.cohortId)}`} className="overview-pulse-row">
      <div className="overview-pulse-head">
        <span className="overview-pulse-name">{bundle.cohortLabel}</span>
        <span className="muted small">{bundle.courseTitle}</span>
      </div>
      <div className="overview-pulse-meta muted small">
        {bundle.rosterTotal} student{bundle.rosterTotal === 1 ? "" : "s"} · {formatPlanItemCounts(countPlanItems(tracked))}
      </div>
      <StackedProgressTrack
        complete={complete}
        inProgress={inProgress}
        needsHelp={needsHelp}
        notStarted={notStarted}
        label={bundle.cohortLabel}
        className="overview-pulse-track"
        emptyLabel="No assigned lessons yet"
      />
      <span className="overview-pulse-pct">{pct(passRate)}</span>
    </Link>
  );
}

export default function TeacherOverviewPage() {
  const { identity, getBearerToken } = useAuth();
  const bearer = getBearerToken();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cohorts, setCohorts] = useState<TeacherCohortRecord[]>([]);
  const [bundles, setBundles] = useState<CohortProgressBundle[]>([]);
  const [deadlines, setDeadlines] = useState<DeadlineRow[]>([]);
  const [lessonCount, setLessonCount] = useState<number | null>(null);
  const [templateCount, setTemplateCount] = useState<number | null>(null);
  const [billing, setBilling] = useState<TeacherBillingSubscriptionResponse | null>(null);

  const reload = useCallback(async () => {
    if (!bearer) return;
    setLoading(true);
    setError(null);
    try {
      const [cohortRes, lessonRes, templateRes, billingRes] = await Promise.allSettled([
        getTeacherCohorts(bearer, { limit: 100, offset: 0 }),
        getLibraryLessons(bearer, { limit: 1, offset: 0 }),
        getTeacherCourses(bearer, { limit: 1, offset: 0 }),
        getTeacherBillingSubscription(bearer),
      ]);

      if (lessonRes.status === "fulfilled") setLessonCount(lessonRes.value.page.total);
      if (templateRes.status === "fulfilled") setTemplateCount(templateRes.value.page.total);
      if (billingRes.status === "fulfilled") setBilling(billingRes.value);

      if (cohortRes.status !== "fulfilled") {
        throw cohortRes.reason instanceof Error ? cohortRes.reason : new Error("Could not load cohorts");
      }

      const active = cohortRes.value.cohorts.filter((c) => c.status === "active");
      setCohorts(active);

      const perCohort = await Promise.allSettled(
        active.map(async (c) => {
          const [prog, assignRes] = await Promise.all([
            getTeacherCohortAssignmentProgress(bearer, c.id),
            getTeacherCohortAssignments(bearer, c.id),
          ]);
          return { cohort: c, prog, assignments: assignRes.assignments };
        })
      );

      const okBundles: CohortProgressBundle[] = [];
      const dueRows: DeadlineRow[] = [];
      let failures = 0;
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      for (const r of perCohort) {
        if (r.status !== "fulfilled") {
          failures += 1;
          continue;
        }
        const { cohort, prog, assignments } = r.value;
        okBundles.push({
          cohortId: cohort.id,
          cohortLabel: cohort.label,
          courseTitle: cohort.courseTitle,
          status: cohort.status,
          rosterTotal: prog.rosterTotal,
          assignments: prog.assignments,
        });
        for (const a of assignments) {
          if (!a.released || a.closed || !a.dueAt) continue;
          const dueAtParsed = parseApiInstant(a.dueAt);
          if (!dueAtParsed) continue;
          const dueAt = dueAtParsed;
          const dueDayStart = new Date(dueAt);
          dueDayStart.setHours(0, 0, 0, 0);
          dueRows.push({
            cohortId: cohort.id,
            cohortLabel: cohort.label,
            assignment: a,
            dueAt,
            daysFromNow: Math.round((dueDayStart.getTime() - todayStart.getTime()) / DAY_MS),
          });
        }
      }

      dueRows.sort((a, b) => a.dueAt.getTime() - b.dueAt.getTime());
      setBundles(okBundles);
      setDeadlines(dueRows);
      if (failures > 0) {
        setError(`Some cohorts failed to load (${failures}). Showing partial data.`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load dashboard");
      setCohorts([]);
      setBundles([]);
      setDeadlines([]);
    } finally {
      setLoading(false);
    }
  }, [bearer]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const totals = useMemo(() => aggregateProgress(bundles), [bundles]);
  const lessonRows = useMemo(() => lessonRowsFromBundles(bundles), [bundles]);
  const needsAttention = useMemo(() => lessonRows.filter((r) => r.needsHelp > 0).slice(0, 4), [lessonRows]);
  const upcomingDeadlines = useMemo(() => deadlines.filter((d) => d.daysFromNow >= -14).slice(0, 6), [deadlines]);

  const now = new Date();
  const greeting = greetingForHour(now.getHours());
  const firstName = firstNameFrom(identity?.name, identity?.email);
  const dateLine = now.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  const trialDaysLeft = useMemo(() => {
    if (billing?.accessPhase !== "app_trial" || !billing.appTrial.endsAt) return null;
    const ends = new Date(billing.appTrial.endsAt).getTime();
    if (Number.isNaN(ends)) return null;
    return Math.max(0, Math.ceil((ends - Date.now()) / DAY_MS));
  }, [billing]);

  const aiUsageLine = useMemo(() => {
    if (!billing || billing.accessPhase !== "app_trial") return null;
    const parts: string[] = [];
    const { usage, limits } = billing;
    if (limits.activeCohorts != null) {
      parts.push(`${usage.activeCohortsUsed} / ${limits.activeCohorts} cohorts`);
    }
    if (limits.activeStudents != null) {
      parts.push(`${usage.activeStudentsUsed} / ${limits.activeStudents} students`);
    }
    if (limits.activeLessons != null) {
      parts.push(`${usage.activeLessonsUsed} / ${limits.activeLessons} lessons`);
    }
    if (limits.aiRequestsPerPeriod != null) {
      parts.push(`${usage.aiLessonRequestsUsed} / ${limits.aiRequestsPerPeriod} AI requests`);
    }
    return parts.length > 0 ? parts.join(" · ") : null;
  }, [billing]);

  const hasCohorts = cohorts.length > 0;

  return (
    <div className="teacher-page overview-dashboard">
      <header className="submissions-hero overview-hero">
        <div className="submissions-hero-text">
          <p className="eyebrow">{dateLine}</p>
          <h1 className="teacher-page-title">
            {greeting}
            {firstName ? `, ${firstName}` : ""} 👋
          </h1>
          <p className="teacher-page-lede">
            {hasCohorts
              ? "Here's the live pulse of your classes: who's cruising, who's stuck, and what's due next."
              : "Let's get your first class up and running."}
          </p>
        </div>
        <div className="overview-hero-actions">
          <Link to="/teacher/lessons/new" className="btn primary">
            <FontAwesomeIcon icon={faWandMagicSparkles} /> New lesson
          </Link>
          <Link to="/teacher/cohorts" className="btn secondary">
            <FontAwesomeIcon icon={faPlus} /> New cohort
          </Link>
        </div>
      </header>

      {trialDaysLeft !== null ? (
        <div className="overview-trial-banner" role="status">
          <strong>
            Trial: {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left.
          </strong>{" "}
          {aiUsageLine ? <span className="muted">{aiUsageLine} · </span> : null}
          <Link to="/teacher/billing">See usage and upgrade</Link>
        </div>
      ) : null}

      {error ? (
        <div className="warn-banner submissions-banner" role="alert">
          {error}
        </div>
      ) : null}

      {loading ? (
        <TeacherOverviewSkeleton />
      ) : !hasCohorts ? (
        <section className="overview-onboarding">
          <h2 className="submissions-chart-title">Three steps to your first class</h2>
          <ol className="overview-onboarding-steps">
            <li className="overview-onboarding-step">
              <span className="overview-step-num">1</span>
              <div>
                <strong>Build a lesson</strong>
                <p className="muted small">Describe what you want and let AI draft it (starter code, tests, and a guided tour), then edit anything in one place.</p>
                <Link to="/teacher/lessons/new" className="btn primary tight">
                  New lesson
                </Link>
              </div>
            </li>
            <li className="overview-onboarding-step">
              <span className="overview-step-num">2</span>
              <div>
                <strong>Bundle it into a course</strong>
                <p className="muted small">Order your lessons into a reusable blueprint you can teach again and again.</p>
                <Link to="/teacher/courses" className="btn secondary tight">
                  Courses
                </Link>
              </div>
            </li>
            <li className="overview-onboarding-step">
              <span className="overview-step-num">3</span>
              <div>
                <strong>Create a cohort and invite students</strong>
                <p className="muted small">Spin up a class roster with a join code, then release lessons when you're ready.</p>
                <Link to="/teacher/cohorts" className="btn secondary tight">
                  Set up a cohort
                </Link>
              </div>
            </li>
          </ol>
        </section>
      ) : (
        <>
          <div className="submissions-kpi-grid overview-kpi-grid">
            <article className="submissions-kpi submissions-kpi--forest">
              <p className="submissions-kpi-label">Students</p>
              <p className="submissions-kpi-value">{totals.rosterStudents.toLocaleString()}</p>
              <p className="submissions-kpi-hint">
                Across {totals.cohortCount} active cohort{totals.cohortCount === 1 ? "" : "s"}
              </p>
            </article>
            <article className="submissions-kpi submissions-kpi--green">
              <p className="submissions-kpi-label">Pass rate</p>
              <p className="submissions-kpi-value">{pct(totals.passRate)}</p>
              <p className="submissions-kpi-hint">
                {totals.complete.toLocaleString()} of {totals.total.toLocaleString()} student-lesson slots complete
              </p>
            </article>
            <article className="submissions-kpi submissions-kpi--amber">
              <p className="submissions-kpi-label">{ASSIGNMENT_PROGRESS_EMOJI.needs_help} Needs help</p>
              <p className="submissions-kpi-value">{totals.needsHelp.toLocaleString()}</p>
              <p className="submissions-kpi-hint">Last Check My Work failed required checks</p>
            </article>
            <article className="submissions-kpi submissions-kpi--slate">
              <p className="submissions-kpi-label">{ASSIGNMENT_PROGRESS_EMOJI.in_progress} In progress</p>
              <p className="submissions-kpi-value">{totals.inProgress.toLocaleString()}</p>
              <p className="submissions-kpi-hint">Working right now, no failed attempts</p>
            </article>
            <article className="submissions-kpi submissions-kpi--forest">
              <p className="submissions-kpi-label">Lessons live</p>
              <p className="submissions-kpi-value">{totals.liveLessons.toLocaleString()}</p>
              <p className="submissions-kpi-hint">Assigned lessons being tracked</p>
            </article>
          </div>

          {needsAttention.length > 0 ? (
            <section className="submissions-attention overview-attention">
              <div className="overview-section-head">
                <h2 className="submissions-chart-title">🚨 Jump in here first</h2>
                <Link to="/teacher/submissions" className="overview-section-link">
                  Full breakdown <FontAwesomeIcon icon={faArrowRight} />
                </Link>
              </div>
              <p className="muted small">Lessons where the most students are stuck after a failed Check My Work</p>
              <div className="submissions-attention-grid">
                {needsAttention.map((r) => {
                  const reviewUrl = teacherAssignmentReviewUrl(r.cohortId, r);
                  if (!reviewUrl) return null;
                  return (
                  <Link
                    key={`${r.cohortId}-${r.assignmentId}`}
                    to={reviewUrl}
                    className="submissions-attention-card"
                  >
                    <span className="submissions-attention-count">{r.needsHelp}</span>
                    <span className="submissions-attention-lesson">{r.lessonTitle}</span>
                    <span className="muted small">{r.cohortLabel}</span>
                  </Link>
                  );
                })}
              </div>
            </section>
          ) : totals.total > 0 ? (
            <div className="overview-all-clear" role="status">
              🎉 Nobody is stuck right now. Every student's latest attempt is either passing or still in flight.
            </div>
          ) : null}

          <div className="overview-main-grid">
            <section className="submissions-chart-panel overview-pulse-panel">
              <div className="overview-section-head">
                <h2 className="submissions-chart-title">Cohort pulse</h2>
                <Link to="/teacher/cohorts" className="overview-section-link">
                  Manage <FontAwesomeIcon icon={faArrowRight} />
                </Link>
              </div>
              <p className="muted small submissions-chart-sub">Completion mix per class. Click through to rosters and lesson plans</p>
              <div className="overview-pulse-list">
                {bundles.map((b) => (
                  <CohortPulseRow key={b.cohortId} bundle={b} />
                ))}
              </div>
            </section>

            <div className="overview-side-col">
              <section className="submissions-chart-panel overview-donut-panel">
                <h2 className="submissions-chart-title">Completion mix</h2>
                <p className="muted small submissions-chart-sub">All cohorts combined</p>
                <ProgressDonut
                  complete={totals.complete}
                  inProgress={totals.inProgress}
                  needsHelp={totals.needsHelp}
                  notStarted={totals.notStarted}
                  passRate={totals.passRate}
                  donutClassName="overview-donut"
                />
              </section>

              <section className="submissions-chart-panel overview-deadlines-panel">
                <h2 className="submissions-chart-title">Deadlines</h2>
                <p className="muted small submissions-chart-sub">Released lessons with a due date</p>
                {upcomingDeadlines.length === 0 ? (
                  <p className="muted small overview-deadlines-empty">
                    Nothing due. Set due dates on assignments to see them here.
                  </p>
                ) : (
                  <ul className="overview-deadline-list">
                    {upcomingDeadlines.map((d) => {
                      const label = dueLabel(d.daysFromNow);
                      const to =
                        d.assignment.itemKind === "quiz" && d.assignment.quizId
                          ? `/teacher/cohorts/${encodeURIComponent(d.cohortId)}/quizzes/${encodeURIComponent(d.assignment.quizId)}`
                          : d.assignment.itemKind === "presentation" && d.assignment.presentationId
                            ? `/teacher/cohorts/${encodeURIComponent(d.cohortId)}/presentations/${encodeURIComponent(d.assignment.presentationId)}/preview`
                            : d.assignment.lessonId
                              ? `/teacher/cohorts/${encodeURIComponent(d.cohortId)}/lessons/${encodeURIComponent(d.assignment.lessonId)}/submissions?${new URLSearchParams({ title: d.assignment.lessonTitle }).toString()}`
                              : "#";
                      return (
                        <li key={d.assignment.id}>
                          <Link to={to} className="overview-deadline-row">
                            <span className={`overview-deadline-chip overview-deadline-chip--${label.tone}`}>
                              {label.text}
                            </span>
                            <span className="overview-deadline-lesson">{d.assignment.lessonTitle}</span>
                            <span className="muted small">{d.cohortLabel}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          </div>
        </>
      )}

      <nav className="overview-shortcut-grid" aria-label="Workspace shortcuts">
        <Link to="/teacher/lessons" className="overview-shortcut">
          <FontAwesomeIcon icon={faBook} className="overview-shortcut-icon" fixedWidth />
          <span className="overview-shortcut-label">My lessons</span>
          <span className="overview-shortcut-stat">{lessonCount === null ? "-" : lessonCount}</span>
        </Link>
        <Link to="/teacher/courses" className="overview-shortcut">
          <FontAwesomeIcon icon={faLayerGroup} className="overview-shortcut-icon" fixedWidth />
          <span className="overview-shortcut-label">Courses</span>
          <span className="overview-shortcut-stat">{templateCount === null ? "-" : templateCount}</span>
        </Link>
        <Link to="/teacher/cohorts" className="overview-shortcut">
          <FontAwesomeIcon icon={faUsers} className="overview-shortcut-icon" fixedWidth />
          <span className="overview-shortcut-label">Cohorts</span>
          <span className="overview-shortcut-stat">{loading ? "-" : cohorts.length}</span>
        </Link>
        <Link to="/teacher/submissions" className="overview-shortcut">
          <FontAwesomeIcon icon={faChartLine} className="overview-shortcut-icon" fixedWidth />
          <span className="overview-shortcut-label">Progress</span>
          <span className="overview-shortcut-stat">{loading ? "-" : pct(totals.passRate)}</span>
        </Link>
        <Link to="/teacher/api-keys" className="overview-shortcut">
          <FontAwesomeIcon icon={faKey} className="overview-shortcut-icon" fixedWidth />
          <span className="overview-shortcut-label">API keys</span>
          <span className="overview-shortcut-stat">→</span>
        </Link>
      </nav>
    </div>
  );
}
